import { TextSource } from './types';

/**
 * Texte der Seite „Kosten".
 *
 * Die Schlüssel folgen dem Aufbau der Seite (Abschnitt, dann Rolle im Abschnitt) —
 * im Bearbeiten-Modus steht der Schlüssel über dem Eingabefeld und soll die Stelle
 * ohne Suchen wiederfinden lassen.
 *
 * `{telefon}` und die übrigen Platzhalter in geschweiften Klammern ersetzt `t()`
 * aus den Stammdaten in `siteData.ts`. So bleibt die Telefonnummer auch dann an
 * einer Stelle gepflegt, wenn der Kunde den Satz drumherum umschreibt.
 */
export const KOSTEN: TextSource = {
  'kosten.hero.eyebrow': 'Kosten & Finanzierung',
  // Überschrift und farbig hervorgehobener Teil sind zwei Texte, damit die
  // Hervorhebung erhalten bleibt, egal wie der Kunde die Zeile umformuliert.
  'kosten.hero.titel': 'Was kostet Betreuung',
  'kosten.hero.titel-akzent': 'zu Hause?',
  'kosten.hero.lead':
    'Wir glauben daran, dass hochwertige Betreuung für jeden erschwinglich sein sollte. Für transparente und faire Preise bieten wir Ihnen exzellenten Service ohne versteckte Kosten — für ein ausgezeichnetes Preis-Leistungs-Verhältnis.',

  'kosten.kopf.karte': { text: 'Die Preise auf einen Blick', max: 40 },
  'kosten.kopf.beispiel': { text: 'Zur Beispielrechnung', max: 40 },
  'kosten.leistungen.titel': 'Kostenübersicht unserer Leistungen',
  'kosten.leistungen.einleitung':
    'Leistungen im Bereich Hauswirtschaft und Alltagsbetreuung als anerkanntes Angebot nach § 45a SGB XI:',

  'kosten.hauswirtschaft.titel': 'Hauswirtschaftliche Leistungen',
  'kosten.hauswirtschaft.posten-1': 'Stundenweise hauswirtschaftliche Leistungen',
  'kosten.hauswirtschaft.preis-1': { text: '37 € / Std.', max: 24 },
  'kosten.hauswirtschaft.posten-2': 'Fahrtkostenpauschale',
  'kosten.hauswirtschaft.preis-2': { text: '6,50 € / Einsatz', max: 24 },

  'kosten.alltagsbetreuung.titel': 'Alltagsbetreuung',
  'kosten.alltagsbetreuung.posten-1': 'Stundenweise Alltagsbetreuung',
  'kosten.alltagsbetreuung.preis-1': { text: '41 € / Std.', max: 24 },
  'kosten.alltagsbetreuung.posten-2': 'Fahrtkostenpauschale',
  'kosten.alltagsbetreuung.preis-2': { text: '6,50 € / Einsatz', max: 24 },

  'kosten.24h.titel': '24-Stunden-Betreuung',
  'kosten.24h.posten-1': 'Mindesttagessatz',
  'kosten.24h.preis-1': { text: '100 €', max: 24 },
  'kosten.24h.posten-2': 'Ø monatliche Kosten',
  'kosten.24h.preis-2': { text: 'ca. 30,5 × 100 €', max: 24 },
  'kosten.24h.posten-3': 'Reisekosten',
  'kosten.24h.preis-3': { text: '140 € / Fahrt', max: 24 },
  // Bewusst ohne Preisangabe daneben — die Zuschläge hängen vom Feiertag ab.
  'kosten.24h.posten-4': 'Zuschläge für ausgewählte Feiertage',

  'kosten.finanzierung.titel': 'Finanzierung & Zuschüsse',
  // Reihenfolge nicht vertauschen: Zu `punkt-1` und `punkt-3` liegt in der
  // Verwaltung ein vom Kunden geänderter Text, der den ausgelieferten
  // überlagert. Wandert der Entlastungsbetrag auf einen anderen Schlüssel,
  // steht er auf der Seite zweimal — einmal aus der Datenbank, einmal von hier.
  'kosten.finanzierung.punkt-1':
    '**Entlastungsbetrag:** 131 € monatlich (§ 45b SGB XI) — für unsere stundenweise Hauswirtschaft und Alltagsbetreuung, nicht für die 24-Stunden-Betreuung',
  'kosten.finanzierung.punkt-2': '**Pflegegeld** je nach Pflegegrad',
  'kosten.finanzierung.punkt-3': '**Verhinderungspflege:** bis zu 3.539 € im Jahr (gemeinsamer Jahresbetrag mit der Kurzzeitpflege)',
  'kosten.finanzierung.eigenanteil':
    'Der monatliche Eigenanteil bzw. Deckungsbeitrag liegt je nach Pflegegrad und Anzahl pflegebedürftiger Personen im Haushalt in der Regel zwischen **1.700 und 2.500 €**.',
  // Vergleich Pflegeheim / ambulanter Dienst / 24-Stunden-Betreuung.
  //
  // Jede Zahl hat eine Quelle, die in `kosten.vergleich.quelle` genannt ist:
  // Eigenanteil Pflegeheim Niedersachsen = vdek-Auswertung zum 1. Juli 2026
  // (Pressemitteilung vom 14.07.2026, Tabelle „Bund und Bundesländer", Spalte NI);
  // Pflegegeld, Pflegesachleistung, Verhinderungspflege = Leistungsbeträge der
  // Pflegeversicherung 2026 (unverändert seit 01.01.2025 bzw. 01.07.2025). Beim
  // ambulanten Dienst steht bewusst keine Eigenanteils-Zahl: Sie hängt von der
  // Zahl der Einsätze ab, jede Pauschale wäre erfunden.
  //
  // Der Entlastungsbetrag (§ 45b SGB XI) taucht in der 24h-Spalte und in der
  // Beispielrechnung nicht auf, und das ist kein Versehen: Er darf nur für ein
  // nach § 45a SGB XI anerkanntes Angebot verwendet werden. Anerkannt ist der
  // Betrieb für die stundenweise Hauswirtschaft und Alltagsbetreuung mit eigenen
  // Mitarbeitern — die 24-Stunden-Betreuung ist eine Vermittlung und fällt nicht
  // darunter. Wer ihn hier wieder einrechnet, verspricht 131 € im Monat, die der
  // Kunde nicht bekommt.
  // Ändern sich die Beträge (nächste Anpassung frühestens 01.01.2028), müssen
  // Tabelle, Beispielrechnung, FAQ 6 und llms.txt zusammen nachgezogen werden.
  'kosten.vergleich.titel': 'Pflegeheim, ambulanter Dienst oder 24-Stunden-Betreuung?',
  'kosten.vergleich.einleitung':
    'Angehörige stehen meist vor genau dieser Frage. Die Tabelle stellt die drei Wege am Beispiel Pflegegrad 3 nebeneinander — mit dem, was die Pflegekasse zahlt, und dem, was am Ende bei Ihnen bleibt.',
  'kosten.vergleich.spalte-merkmal': { text: 'Merkmal', max: 30 },
  'kosten.vergleich.spalte-heim': { text: 'Pflegeheim', max: 30 },
  'kosten.vergleich.spalte-ambulant': { text: 'Ambulanter Pflegedienst', max: 40 },
  'kosten.vergleich.spalte-24h': { text: '24-Stunden-Betreuung zu Hause', max: 40 },

  'kosten.vergleich.zeile-1.merkmal': { text: 'Wer ist da?', max: 40 },
  'kosten.vergleich.zeile-1.heim':
    'Pflegefachkräfte im Schichtdienst — zuständig für viele Bewohner zugleich.',
  'kosten.vergleich.zeile-1.ambulant':
    'Fachkräfte kommen zu festen Einsätzen für einzelne Handgriffe; dazwischen ist niemand da.',
  'kosten.vergleich.zeile-1.24h':
    'Eine Betreuungskraft wohnt im Haushalt und ist rund um die Uhr ansprechbar — nur für Ihren Angehörigen.',

  'kosten.vergleich.zeile-2.merkmal': { text: 'Wo wird gewohnt?', max: 40 },
  'kosten.vergleich.zeile-2.heim': 'Umzug ins Heim, meist Einzelzimmer.',
  'kosten.vergleich.zeile-2.ambulant': 'Zu Hause.',
  'kosten.vergleich.zeile-2.24h': 'Zu Hause — die Betreuungskraft braucht ein eigenes Zimmer.',

  'kosten.vergleich.zeile-3.merkmal': { text: 'Was zahlt die Pflegekasse?', max: 40 },
  'kosten.vergleich.zeile-3.heim':
    'Einen festen Leistungsbetrag direkt ans Heim; dazu ab dem zweiten Jahr einen Zuschlag auf den Eigenanteil.',
  'kosten.vergleich.zeile-3.ambulant':
    'Pflegesachleistung bis 1.497 € im Monat (Pflegegrad 3), direkt an den Dienst.',
  'kosten.vergleich.zeile-3.24h':
    'Pflegegeld 599 € im Monat (Pflegegrad 3) plus Verhinderungspflege bis 3.539 € im Jahr.',

  'kosten.vergleich.zeile-4.merkmal': { text: 'Was bleibt bei Ihnen?', max: 40 },
  'kosten.vergleich.zeile-4.heim':
    'In Niedersachsen im ersten Jahr durchschnittlich **3.008 € im Monat**, ab dem vierten Jahr 1.790 € (Stand 1. Juli 2026).',
  'kosten.vergleich.zeile-4.ambulant':
    'Alles, was über den Sachleistungsbetrag hinausgeht — je nach Zahl der Einsätze; die Stunden dazwischen tragen die Angehörigen.',
  'kosten.vergleich.zeile-4.24h':
    'Bei rund 3.050 € im Monat und Pflegegrad 3 **etwa 2.150 € im Monat** — siehe Beispielrechnung.',

  'kosten.vergleich.zeile-5.merkmal': { text: 'Medizinische Pflege', max: 40 },
  'kosten.vergleich.zeile-5.heim': 'Im Haus.',
  'kosten.vergleich.zeile-5.ambulant': 'Durch den Dienst, verordnet vom Arzt.',
  'kosten.vergleich.zeile-5.24h':
    'Weiterhin durch den ambulanten Dienst — beides lässt sich kombinieren.',

  'kosten.vergleich.quelle':
    'Quellen: Eigenanteile im Pflegeheim nach der Auswertung des Verbands der Ersatzkassen (vdek) zum 1. Juli 2026, Durchschnitt Niedersachsen; Leistungsbeträge der Pflegeversicherung 2026. Durchschnittswerte — das einzelne Heim und der einzelne Haushalt weichen davon ab.',

  // Beispielrechnung — dieselben Zahlen wie in der Tabelle und im FAQ, einmal
  // ausgerechnet. Die Verhinderungspflege ist ein Jahresbetrag, hier auf zwölf
  // Monate verteilt (3.539 € / 12 ≈ 295 €). Reisekosten und Feiertagszuschläge
  // stehen bewusst daneben, nicht in der Summe: Sie fallen nicht monatlich an.
  'kosten.beispiel.titel': 'Beispielrechnung: 24-Stunden-Betreuung bei Pflegegrad 3',
  'kosten.beispiel.einleitung':
    'So rechnet sich ein Monat für eine Person mit Pflegegrad 3, die zu Hause bleibt — vereinfacht, ohne Reisekosten und Feiertagszuschläge:',
  'kosten.beispiel.posten-1': { text: 'Betreuung, 30,5 Tage × 100 €', max: 50 },
  'kosten.beispiel.wert-1': { text: '3.050 €', max: 16 },
  'kosten.beispiel.posten-2': { text: 'abzüglich Pflegegeld Pflegegrad 3', max: 50 },
  'kosten.beispiel.wert-2': { text: '− 599 €', max: 16 },
  'kosten.beispiel.posten-4': { text: 'abzüglich Verhinderungspflege (3.539 € im Jahr, auf 12 Monate verteilt)', max: 80 },
  'kosten.beispiel.wert-4': { text: '− 295 €', max: 16 },
  'kosten.beispiel.summe': { text: 'Eigenanteil im Monat', max: 40 },
  'kosten.beispiel.summe-wert': { text: 'rund 2.156 €', max: 20 },
  'kosten.beispiel.hinweis':
    'Dazu kommen 140 € Reisekosten je Fahrt beim Wechsel der Betreuungskräfte sowie Zuschläge an ausgewählten Feiertagen. Bei Pflegegrad 4 oder 5 ist das Pflegegeld höher, der Eigenanteil entsprechend niedriger. Der Entlastungsbetrag steht bewusst nicht in dieser Rechnung — er ist an ein nach § 45a SGB XI anerkanntes Angebot gebunden und lässt sich für unsere stundenweisen Leistungen einsetzen, nicht für die 24-Stunden-Betreuung. Ob und wie die Verhinderungspflege für Ihre Situation genutzt werden kann, klären wir gemeinsam mit Ihrer Pflegekasse.',

  // Was der Tagessatz abdeckt. Laut Phil (28.09.2026) kommt außer Reisekosten
  // und Feiertagszuschlägen nichts dazu — ändert sich das, diesen Satz mitziehen.
  'kosten.tagessatz.titel': 'Was im Tagessatz steckt',
  'kosten.tagessatz.text':
    'Lohn und Sozialversicherung der Betreuungskraft sind im Tagessatz enthalten. Dazu kommen nur die Reisekosten beim Wechsel und die Feiertagszuschläge.',

  'kosten.finanzierung.abrechnung':
    'Unsere Leistungen nach § 45a SGB XI können direkt mit den zuständigen Pflegekassen abgerechnet werden. Voraussetzung ist die Genehmigung durch die jeweilige Pflegekasse.',

  // Die Fragen speisen zugleich das FAQ-Markup für Google. Wer hier eine Antwort
  // ändert, ändert damit auch, was in den Suchergebnissen erscheinen kann —
  // deshalb dürfen die Angaben nie von den Preisen weiter oben abweichen.
  'kosten.faq.titel': 'Häufige Fragen zu den Kosten',
  'kosten.faq.1.frage': 'Was kostet eine Haushaltshilfe in Gifhorn?',
  'kosten.faq.1.antwort':
    'Stundenweise hauswirtschaftliche Leistungen kosten 37 € pro Stunde, die Alltagsbetreuung 41 € pro Stunde. Dazu kommt eine Fahrtkostenpauschale von 6,50 € je Einsatz.',
  'kosten.faq.2.frage': 'Was kostet eine 24-Stunden-Betreuung im Monat?',
  'kosten.faq.2.antwort':
    'Der Mindesttagessatz liegt bei 100 €, im Monat also bei rund 3.050 €. Hinzu kommen 140 € Reisekosten je Fahrt sowie Zuschläge für ausgewählte Feiertage. Nach Abzug der Leistungen der Pflegekasse liegt der Eigenanteil je nach Pflegegrad in der Regel zwischen 1.700 und 2.500 € im Monat.',
  'kosten.faq.3.frage': 'Welche Zuschüsse gibt es von der Pflegekasse?',
  'kosten.faq.3.antwort':
    'Zur 24-Stunden-Betreuung steuert die Kasse das Pflegegeld je nach Pflegegrad bei sowie die Verhinderungspflege mit bis zu 3.539 € im Jahr — dem gemeinsamen Jahresbetrag mit der Kurzzeitpflege. Der Entlastungsbetrag nach § 45b SGB XI von 131 € monatlich kommt dazu, wenn Sie unsere stundenweise Hauswirtschaft oder Alltagsbetreuung in Anspruch nehmen: Er darf nur für ein nach § 45a SGB XI anerkanntes Angebot verwendet werden und lässt sich deshalb nicht auf die 24-Stunden-Betreuung anrechnen.',
  'kosten.faq.4.frage': 'Kann ich die Leistungen direkt mit der Pflegekasse abrechnen?',
  'kosten.faq.4.antwort':
    'Ja. Unsere Leistungen nach § 45a SGB XI rechnen wir direkt mit der zuständigen Pflegekasse ab. Voraussetzung ist die Genehmigung durch die jeweilige Pflegekasse — bei der Beantragung helfen wir Ihnen.',
  'kosten.faq.5.frage': 'Wie komme ich zu einem persönlichen Angebot?',
  'kosten.faq.5.antwort':
    'Rufen Sie uns unter {telefon} an oder schreiben Sie uns über das Kontaktformular. Auf Wunsch kommen wir zu Ihnen nach Hause, ermitteln gemeinsam den Bedarf und erstellen ein individuelles Angebot. Das Kennenlerngespräch ist kostenlos und unverbindlich.',

  'kosten.angebot.titel': 'Ihr persönliches Angebot',
  'kosten.angebot.text':
    'Gerne erstellen wir Ihnen ein persönliches Angebot. Dazu kommen wir auch gerne zu Ihnen und führen eine individuelle Bedarfsanalyse sowie ein Beratungsgespräch durch. Kontaktieren Sie uns für weitere Informationen oder einen Termin.',
  'kosten.angebot.cta': { text: 'Angebot anfragen', label: 'Beschriftung der grünen Schaltfläche' },
  'kosten.faq.6.frage': 'Ist die 24-Stunden-Betreuung günstiger als ein Pflegeheim?',
  'kosten.faq.6.antwort':
    'Bei Pflegegrad 3 meist ja. Ein Heimplatz kostet in Niedersachsen im ersten Jahr durchschnittlich 3.008 € Eigenanteil im Monat (vdek, Stand 1. Juli 2026). Die 24-Stunden-Betreuung zu Hause liegt nach Abzug von Pflegegeld und Verhinderungspflege bei rund 2.150 € im Monat — und Ihr Angehöriger bleibt in den eigenen vier Wänden. Ab dem vierten Heimjahr sinkt der Eigenanteil im Heim durch den Zuschlag der Pflegekasse deutlich; dann kehrt sich der Vergleich um.',
  'kosten.faq.7.frage': 'Bei wem ist die Betreuungskraft angestellt?',
  'kosten.faq.7.antwort':
    'Bei unserem Unternehmen Antczak Care sp. z o.o. in Polen, wo sie auch sozialversichert ist. Ihren Vertrag schließen Sie ebenfalls mit Antczak Care. Ansprechpartner vor Ort sind wir in Gifhorn.',
};

export default KOSTEN;
